'use client';

/**
 * FF-481 — Session expiry guard.
 *
 * Watches NextAuth's session via `useSession()`. SessionProvider's
 * `refetchInterval=120` polls `/api/auth/session` every 2 min; when the server
 * returns no user (JWT expired or missing), status flips to `unauthenticated`
 * and we render a non-dismissible modal nudging the operator to refresh +
 * re-login before trying to submit anything.
 *
 * Paired with the rolling JWT refresh in `src/lib/auth.ts` so active operators
 * never silently expire — the modal appears only after idle timeout, logout,
 * or a secret rotation.
 *
 * Hidden on `/login` + public routes so unauthenticated visitors to the login
 * page don't see the modal.
 */
import { useSession } from 'next-auth/react';
import { usePathname } from 'next/navigation';
import { useEffect, useState } from 'react';

const EXEMPT_PREFIXES = ['/login', '/staff-login', '/reset-password', '/forgot-password', '/privacy', '/report'];

export function SessionExpiryGuard() {
  const { status } = useSession();
  const pathname = usePathname();
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    if (status === 'loading') return;
    const onExemptPage = EXEMPT_PREFIXES.some(p => pathname?.startsWith(p));
    if (onExemptPage) {
      setShowModal(false);
      return;
    }
    if (status === 'unauthenticated') {
      setShowModal(true);
    } else {
      setShowModal(false);
    }
  }, [status, pathname]);

  if (!showModal) return null;

  const refreshAndLogin = () => {
    window.location.href = '/login?next=' + encodeURIComponent(pathname || '/');
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="session-expiry-title"
      style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        background: 'rgba(10, 10, 10, 0.72)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '16px',
      }}
    >
      <div
        style={{
          background: 'white', borderRadius: '12px',
          padding: '28px 32px', maxWidth: '440px', width: '100%',
          boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
          fontFamily: '-apple-system, "SF Pro Text", "Inter", "Segoe UI", Roboto, sans-serif',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '14px' }}>
          <span style={{
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            width: '32px', height: '32px', borderRadius: '8px',
            background: '#fef2ea', color: '#e85d1c', fontSize: '20px', fontWeight: 700,
          }}>!</span>
          <h2 id="session-expiry-title" style={{ fontSize: '18px', fontWeight: 700, color: '#0a0a0a', margin: 0 }}>
            Sesi Anda sudah berakhir
          </h2>
        </div>
        <p style={{ fontSize: '14px', color: '#424242', lineHeight: 1.5, marginBottom: '8px' }}>
          Sesi login Anda sudah tidak aktif. Silakan <b>refresh halaman dan login ulang</b> sebelum melanjutkan.
        </p>
        <p style={{ fontSize: '13px', color: '#dc2626', lineHeight: 1.4, marginBottom: '20px', fontWeight: 500 }}>
          ⚠ Data form yang belum di-submit akan hilang setelah login ulang. Catat atau screenshot dulu kalau perlu.
        </p>
        <button
          type="button"
          onClick={refreshAndLogin}
          style={{
            width: '100%', padding: '12px 16px',
            background: '#0a0a0a', color: 'white',
            border: 'none', borderRadius: '8px',
            fontSize: '14px', fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          Refresh &amp; Login Ulang
        </button>
      </div>
    </div>
  );
}
